import React, { Suspense, Fragment, useContext, useEffect } from "react";
import { Router, Switch, Route } from "react-router-dom";
import { MuiPickersUtilsProvider } from "@material-ui/pickers";
import MomentUtils from "@date-io/moment";
import { routes } from "src/routes";
import { createBrowserHistory } from "history";
import { Toaster } from "react-hot-toast";
import PageLoading from "src/component/PageLoading";
import { ThemeProvider } from "@material-ui/core";
import { createThemeWeb } from "src/theme";
import SettingsContext from "src/context/SettingsContext";
import AuthContext from "src/context/Auth";
import AuthGuard from "./component/AuthGuard";
import { GlobalStyles } from "@mui/material";
import { useHandleLogin } from "src/firebase"
import { LicenseInfo } from '@mui/x-license';
import { RefetchProvider } from 'src/context/RefetchContext'
import { useWalletSyncNotifier } from "src/WalletSyncNotifier";
import { SnackbarProvider } from "src/context/SnackbarProvider";
import { RefreshProvider } from "src/context/RefreshContext";

const history = createBrowserHistory();
function App() {
  LicenseInfo.setLicenseKey(process.env.REACT_APP_MUI_LICENSE_KEY);
  //This line of code to Disable the console logs from the production Build
  if (process.env.REACT_APP_IS_CONSOLE_LOG == 'false') {
    console.log = () => { };
    console.error = () => { };
    console.warn = () => { };
  }

  if (process.env.REACT_APP_ENV === 'production') {
    // Google Tag Manager Script
    (function (w, d, s, l, i) {
      w[l] = w[l] || [];
      w[l].push({
        'gtm.start': new Date().getTime(),
        event: 'gtm.js'
      });
      var f = d.getElementsByTagName(s)[0],
        j = d.createElement(s),
        dl = l !== 'dataLayer' ? '&l=' + l : '';
      j.async = true;
      j.src = 'https://www.googletagmanager.com/gtm.js?id=' + i + dl;
      f.parentNode.insertBefore(j, f);
    })(window, document, 'script', 'dataLayer', 'GTM-TVD6CGKL');

    // Google Tag Manager <noscript> iframe
    const noscript = document.createElement('noscript');
    noscript.innerHTML = `
    <iframe src="https://www.googletagmanager.com/ns.html?id=GTM-TVD6CGKL"
            height="0" width="0" style="display:none;visibility:hidden"></iframe>
  `;
    document.body.appendChild(noscript);
  }


  useHandleLogin();
  const themeSeeting = useContext(SettingsContext);
  useWalletSyncNotifier();
  const theme = createThemeWeb({
    theme: themeSeeting.settings.theme,
    components: {
      MuiTypography: {
        styleOverrides: {
          body2: {
            // fontSize: '1rem', // Your desired font size
            //fontFamily: '"Roboto", sans-serif', // Your desired font family
            fontWeight: 400, // Your desired font weight
            //lineHeight: 1.7, // Your desired line height
            //letterSpacing: '0.05rem', // Your desired letter spacing
          },
        },
      },
    },

  });

  // Dynamic Tab Title on Route Change
  useEffect(() => {
    const pageTitles = {
      "/": "Home",
      "/secure/portfolio": "Portfolio",
      "/secure/transactions": "Transaction",
      "/secure/wallet": "Wallets",
      // "/vote": "Vote",
      "/secure/add-assets": "Assets",
      "/secure/onBoarding": "Onboarding",
      "/secure/add-wallet": "Add wallet",
      "/secure/profile": "Profile",
      "/secure/tax": "Tax Center",
      "/secure/performance": "Performance",
      "/secure/tax-write-offs": "Tax Write-offs",
      "/secure/accountant": "CPA Portal",
      "/secure/payment-processing": "Payment Processing",
      "/secure/payment-cancelled": "Payment Cancelled",
    };

    // Listen for route changes and update the title
    const unlisten = history.listen((location) => {
      document.title = `Blockstats | ${pageTitles[location.pathname] || "App"} `;
    });

    // Set initial title when app loads
    document.title = `Blockstats | ${pageTitles[window.location.pathname] || "App"} `;

    return () => unlisten(); // Cleanup listener on unmount
  }, []);

  return (
    <div className="App">
      <ThemeProvider theme={theme}>
        <MuiPickersUtilsProvider utils={MomentUtils}>
          <RefetchProvider>
            <AuthContext>
              <SnackbarProvider>
                <RefreshProvider>
                  <Toaster
                    position="top-right"
                    autoClose={3000}
                    hideProgressBar={false}
                    newestOnTop={false}
                    closeOnClick
                    rtl={false}
                    pauseOnFocusLoss
                    draggable
                    pauseOnHover
                    // theme="light"
                    theme={theme.palette.type}
                  />
                  <Router history={history}>
                    <RenderRoutes data={routes} />
                  </Router>
                </RefreshProvider>
              </SnackbarProvider>
            </AuthContext>
          </RefetchProvider>
        </MuiPickersUtilsProvider>
      </ThemeProvider>
    </div>
  );
}

export default App;

function RenderRoutes(props) {
  return (
    <Suspense fallback={<PageLoading />}>
      <Switch>
        {props.data.map((route, i) => {
          const Component = route.component;
          const Layout = route.layout || Fragment;

          // ✅ Updated: Support both boolean guards and custom guard components
          let Guard = Fragment;

          if (route.guard) {
            // If guard is a function/component (custom guard like LoginRedirectGuard)
            if (typeof route.guard === 'function') {
              Guard = route.guard;
            }
            // If guard is true (boolean), use default AuthGuard
            else if (route.guard === true) {
              Guard = AuthGuard;
            }
          }

          return (
            <Route
              key={i}
              exact={route.exact}
              path={route.path}
              render={(props) => (
                <Guard>
                  <Layout>
                    {route.routes ? (
                      <RenderRoutes data={route.routes} />
                    ) : (
                      <Component {...props} />
                    )}
                  </Layout>
                </Guard>
              )}
            />
          );
        })}
      </Switch>
    </Suspense>
  );
}
